import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { fetchDataByConfig, getCitiesList } from '../../ManageDealDrawer/ManageDealDrawerService';
import { fetchAllAvailableProducts, fetchCompanySectorsList } from '../../../store/investor/apiHelpers';
import { DEAL, DealStatus, MANDATE } from '../../../store/investor/reducer';
import { DEAL_VIEW_TYPES } from '../../../store/investor/constants';
import { useAppSelector } from '../../../app/hooks';
import { useSearchParams } from 'react-router-dom';
import { DataTableColumn } from './DataTable.types';

type Option = { value: string | null; label: string };

/**
 * Maps a filter source key to the dynamic filters it needs.
 * Add or remove flags here to customize API fetching per screen.
 */
const SOURCE_FILTER_CONFIG: Record<
    string,
    {
        fetchLenders?: boolean;
        fetchProducts?: boolean;
        fetchCities?: boolean;
        fetchSectors?: boolean;
    }
> = {
    MANDATE_VIEW: { fetchLenders: true, fetchProducts: true },
    DEAL_VIEW: { fetchLenders: true, fetchProducts: true },
    DEALS: { fetchLenders: true, fetchProducts: true }, // Added for deals page
    LEADS_PAGE: {
        fetchCities: true,
        fetchSectors: true,
    },
    MANAGE_LENDERS: { fetchCities: true, fetchProducts: true },
    MANAGE_PARTNERS: { fetchCities: true, fetchProducts: true, fetchSectors: true },
};

const useDynamicFilterOptions = (filterSourceKey: string = '', columns: DataTableColumn[]) => {
    const [lenders, setLenders] = useState<Option[]>([]);
    const [products, setProducts] = useState<Option[]>([]);
    const [cities, setCities] = useState<Option[]>([]);
    const [states, setStates] = useState<Option[]>([]);
    const [sectors, setSectors] = useState<Option[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchParams] = useSearchParams();
    const userData = useAppSelector((state) => state.user.userData);
    const owners = useAppSelector((state) => state.investor.ownerDropdownOptions) || [];
    const dealStatusConfig = useAppSelector((state) => state.investor.deals.dealStatusConfig);
    const selectedCategory = searchParams.get('category') || DEAL_VIEW_TYPES.DEAL_VIEW;
    const isFilterDrawerOpen = useAppSelector((state) => state.investor.customFilterDrawer.open);
    const animationCompleteRef = useRef(false);
    const partnerData = useAppSelector((state) => state.investor.partnerData);

    /**
     * Fetch all dynamic filter options based on config for current source key.
     */
    const fetchAllOptions = useCallback(async () => {
        const config = SOURCE_FILTER_CONFIG[filterSourceKey] || {};

        try {
            setIsLoading(true);
            const promiseMap: Record<string, Promise<any>> = {};

            if (config.fetchLenders) {
                promiseMap.lenders = fetchDataByConfig({ parentOrgId: userData.investorOrgId }, 'GET_ALL_LENDERS', 'GET');
            }

            if (config.fetchProducts) {
                promiseMap.products = fetchAllAvailableProducts({ aggregatorId: userData.investorOrgId }, true);
            }

            let sectorRes: any = null;
            if (config.fetchSectors) {
                sectorRes = await fetchCompanySectorsList();
            }

            // Handle cities API separately to avoid Promise.all issues
            let citiesRes: any = null;
            if (config.fetchCities) {
                try {
                    citiesRes = await getCitiesList({ parentOrgId: userData.investorOrgId });
                } catch (error) {
                    console.error('Cities API error:', error);
                    citiesRes = null;
                }
            }

            const keys = Object.keys(promiseMap);
            const results = await Promise.all(Object.values(promiseMap)).catch((error) => {
                console.error('Promise.all error:', error);
                return keys.map(() => null);
            });
            const resolved: Record<string, any> = {};
            keys.forEach((key, i) => {
                resolved[key] = results[i];
            });

            // Only update state if animation is complete
            if (!animationCompleteRef.current) {
                return;
            }

            if (config.fetchLenders && resolved.lenders) {
                const lenderOptions = resolved.lenders?.data?.lenderMap?.ADMIN
                    ? Object.entries(resolved.lenders.data.lenderMap.ADMIN).map(([value, label]: [string, any]) => ({
                          value,
                          label,
                      }))
                    : [];
                setLenders(lenderOptions);
            }

            if (config.fetchProducts && resolved.products) {
                const productOptions = resolved.products
                    ? Object.values(resolved.products).map((item: any) => ({
                          value: item?.productId || '',
                          label: item?.productName || '',
                      }))
                    : [];
                setProducts(productOptions);
            }

            if (config.fetchCities && citiesRes && citiesRes.data) {
                const responseBody = citiesRes.data?.responseBody?.AICA_GET_CITIES_LIST;
                const citiesArr = responseBody?.cities;
                if (Array.isArray(citiesArr)) {
                    const cityOptions = citiesArr.map((city: string) => ({ value: city, label: city }));
                    setCities(cityOptions);
                } else {
                    setCities([]);
                }
                const statesArr = responseBody?.states;
                if (Array.isArray(statesArr)) {
                    const stateOptions = statesArr.map((state: string) => ({ value: state, label: state }));
                    setStates(stateOptions);
                } else {
                    setStates([]);
                }
            }
            if (config.fetchSectors && sectorRes) {
                let sectorOptions = sectorRes.AICA_GET_COMPANY_SECTOR_LIST?.data?.positiveSector;
                // Handle case where sectorOptions might be a JSON string
                if (Array.isArray(sectorOptions)) {
                    const mappedSectors = sectorOptions.map((sectorObj: any) => ({
                        value: sectorObj._id ?? '',
                        label: sectorObj.label ?? '',
                    }));
                    setSectors(mappedSectors);
                } else {
                    setSectors([]);
                }
            }
        } catch (err) {
            console.error('Error fetching dropdown options:', err);
            console.error('Error details:', err);
        } finally {
            setIsLoading(false);
        }
    }, [filterSourceKey, userData?.investorOrgId]);

    /**
     * Run fetch when drawer opens and userData is ready.
     * Delay the fetch to allow the background animation to complete smoothly.
     */
    useEffect(() => {
        if (isFilterDrawerOpen && userData?.investorOrgId) {
            // Kick the options fetch off immediately on open — the request is async
            // and doesn't jank the slide; the previous 600ms artificial wait just
            // left the drawer on a spinner that much longer before data arrived.
            // animationCompleteRef still guards against a state update after close.
            animationCompleteRef.current = true;
            fetchAllOptions();
        } else if (!isFilterDrawerOpen) {
            // Reset the flag when drawer closes
            animationCompleteRef.current = false;
        }
        setIsLoading(false);
    }, [isFilterDrawerOpen, fetchAllOptions, userData?.investorOrgId]);

    /**
     * Owner dropdown options — derived from Redux.
     */
    const ownerOptions = useMemo<Option[]>(() => {
        return owners.map((owner) => ({
            value: owner?.userId || '',
            label: owner?.name || '',
        }));
    }, [owners]);

    /**
     * Status dropdown options — dynamic based on deal view type.
     */
    const dropdownStatusOptions = useMemo(() => {
        const isInvestorDealView = selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW;
        let statuses = selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW ? dealStatusConfig?.[DEAL] || [] : dealStatusConfig?.[MANDATE] || [];
        if (isInvestorDealView) {
            statuses = statuses.filter((option) => option.value !== DealStatus.INTERESTED);
        }
        return statuses;
    }, [selectedCategory]);

    /**
     * Final map of dynamic options for each field used in filter config.
     */
    const filterOptions: Record<string, Option[]> = useMemo(() => {
        const base: Record<string, Option[]> = {
            lenderName: lenders,
            loanTypes: products,
            dealStatus: dropdownStatusOptions,
        };

        // Assign owner options to any field that starts with "owners"
        columns.forEach((column) => {
            if (column?.field && column.field.startsWith('owners')) {
                base[column.field] = ownerOptions;
            }
        });

        // Handle organizationOwner field
        columns.forEach((column) => {
            if (column?.field && column.field === 'organizationOwner') {
                base[column.field] = ownerOptions;
            }
        });

        // Assign product options to any field containing 'products'
        columns.forEach((column) => {
            if (column?.field && column.field.toLowerCase().includes('products')) {
                base[column.field] = products;
            }
        });

        // Assign city options to any field containing '.city'
        columns.forEach((column) => {
            if (column?.field && column.field.toLowerCase().includes('.city')) {
                base[column.field] = cities;
            }
        });
        // Assign state options to any field containing 'states'
        columns.forEach((column) => {
            if (column?.field && column.field.toLowerCase().includes('states')) {
                base[column.field] = states;
            }
        });
        columns.forEach((column) => {
            if (column?.field && column.field.toLowerCase().includes('.sector')) {
                base[column.field] = sectors;
            }
        });

        // Use static filterConfig.options for any column that provides them
        columns.forEach((column) => {
            if (column?.field && column?.filterConfig?.options && Array.isArray(column?.filterConfig?.options)) {
                base[column.field] = column?.filterConfig?.options;
            }
        });

        if (partnerData) {
            base['customFields.partnerOrgId'] = partnerData
                .filter((org) => org?.organizationId && org?.organizationDisplayName)
                .map((org) => ({
                    value: org.organizationId || '',
                    label: org.organizationDisplayName || '',
                }));
        }

        // Add null value option (e.g., "Not Set") to fields based on nullValueOption config
        columns.forEach((column) => {
            const nullConfig = column?.filterConfig?.nullValueOption;

            if (column?.field && nullConfig && base[column.field]) {
                const nullOption = {
                    label: nullConfig.label || 'Not Set',
                    value: null,
                };

                // Position at top or bottom based on config (default: top)
                if (nullConfig.position === 'bottom') {
                    base[column.field] = [...base[column.field], nullOption];
                } else {
                    base[column.field] = [nullOption, ...base[column.field]];
                }
            }
        });

        return base;
    }, [lenders, products, dropdownStatusOptions, ownerOptions, cities, states, columns, partnerData]);

    return { filterOptions, isLoading };
};

export default useDynamicFilterOptions;
